import _ from "lodash";
import React from "react";
import { withTranslation } from "react-i18next";

import submit from "submit-form";
import { defaultFirebaseProjectId } from "../../../constants";
import { SearchDropdown } from "../../../parts/inputs/search-dropdown";
import Select from "../../../parts/inputs/select";
import Modal from "../../../parts/modal-new";
import OptionToggler from "../../../parts/OptionToggler";
import { RillContext } from "../../../rill/RillReact/share/base";
import {
  cycleHasCopyableBlock,
  cycleHasNoEmptyWorkouts,
  cycleOwnerId,
  programByOrder,
  programHasNoEmptyWorkouts,
} from "../../../utils/copiable-cycle";
import { getUserAccess } from "../../../utils/organization-helpers";
import AccordionRadio from "./AccordionRadio";
import { clearProgramFetch, currentProgramFetch } from "./program-fetch-entry";

const TYPES_MAP = {
  template: "coach",
  otherAthlete: "coach",
  store: "subscription",
};

// Cleared on every close. refresh-with drops empty values, so listing a key
// here removes it from the page URL instead of leaving the last entry behind.
const CLEAR_ON_CLOSE = [
  "sendToAnother",
  "destinationCycleId",
  "destinationCycleName",
  "destinationCycleStatus",
  "fetchSourceOwner",
  "fetchType",
  "fetchEntry",
];

class FetchCycleModal extends React.Component {
  constructor(props, context) {
    super(props, context);
    const coachId = String(context.locals.activeUserToRequest._id);
    const queryType = context.locals.query?.fetchType;
    const fetchSourceOwner = context.locals.query?.fetchSourceOwner
      ? String(context.locals.query.fetchSourceOwner)
      : "";
    const fetchType = ["template", "otherAthlete", "store"].includes(queryType)
      ? queryType
      : fetchSourceOwner && fetchSourceOwner !== coachId
        ? "otherAthlete"
        : "template";
    this.state = {
      fetchType,
      sourceOwner: fetchType === "otherAthlete" ? fetchSourceOwner : coachId,
      sourceCycleId: "",
      programOrder: "",
      submited: false,
      showValidationError: false,
      showCopyError: false,
      showGenericCopyError: false,
      programsLoading: false,
    };
    this._seenLocals = context.locals;
    this._sending = false;
    this._expectedOwner = "";
    this._expectedType = "";
    this._resentFor = "";
    this._resentQuery = "";
    this._querySignature = this.querySignature(context.locals.query || {});
  }

  querySignature(query) {
    return [
      query.destinationCycleId || "",
      query.fetchType || "",
      query.fetchSourceOwner || "",
      query.destinationCycleStatus || "",
      query.fetchEntry || "",
    ].join("|");
  }

  componentWillUnmount() {
    this._unmounted = true;
    // Close and a successful copy both unmount the modal. Drop the open
    // token so browser Back restores the URL that was actually open.
    clearProgramFetch();
  }

  componentDidMount() {
    this.maybeReassertProgramFetch(this.context.locals.query || {}, true);
  }

  // A whole-program open from the header is newer than a client refresh that
  // was already in flight. Put that open back if a late response replaced it.
  maybeReassertProgramFetch(query, localsChanged) {
    const token = currentProgramFetch();
    if (!token) return false;
    const coachId = String(this.context.locals.activeUserToRequest._id);
    const matches =
      String(query.fetchEntry || "") === token &&
      !query.destinationCycleId &&
      query.fetchType === "template" &&
      String(query.fetchSourceOwner || "") === coachId;
    if (matches) {
      if (this.state.programsLoading) this.setState({ programsLoading: false });
      return false;
    }
    if (!localsChanged) return false;
    if (this._reasserted === token && this._reassertCount >= 2) return false;
    this._reasserted = token;
    this._reassertCount = (this._reassertCount || 0) + 1;
    if (!this.state.programsLoading) this.setState({ programsLoading: true });
    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "fetch",
        fetchType: "template",
        fetchSourceOwner: coachId,
        destinationCycleId: "",
        destinationCycleName: "",
        destinationCycleStatus: "created",
        fetchEntry: token,
      },
    });
    return true;
  }

  componentDidUpdate() {
    const locals = this.context.locals;
    const query = locals.query || {};
    const localsChanged = this._seenLocals && this._seenLocals !== locals;
    if (localsChanged) this._seenLocals = locals;

    if (this.maybeReassertProgramFetch(query, localsChanged)) return;

    // Success closes the modal. If a send was in flight and the page came
    // back with the modal still open, release the button.
    if (this.state.submited && localsChanged) {
      this.setState({ submited: false });
    }

    const coachId = String(locals.activeUserToRequest._id);
    const signature = this.querySignature(query);
    const signatureChanged = signature !== this._querySignature;
    if (signatureChanged) this._querySignature = signature;

    const queryOwner = String(query.fetchSourceOwner || "");
    const selectedOwner = String(this.state.sourceOwner || "");
    const ownerRefresh =
      !!this._expectedOwner &&
      queryOwner === this._expectedOwner &&
      String(query.fetchType || "") === this._expectedType;

    // Header open (and any other whole-program entry that sets fetchEntry)
    // must not keep a destination, tab, client, or selection from last time.
    const cleanProgramOpen =
      signatureChanged &&
      !ownerRefresh &&
      !!query.fetchEntry &&
      !query.destinationCycleId &&
      query.fetchType === "template" &&
      queryOwner === coachId;
    if (
      cleanProgramOpen &&
      (this.state.fetchType !== "template" ||
        selectedOwner !== coachId ||
        this.state.sourceCycleId ||
        this.state.programOrder)
    ) {
      this._expectedOwner = "";
      this._expectedType = "";
      this.setState({
        fetchType: "template",
        sourceOwner: coachId,
        sourceCycleId: "",
        programOrder: "",
        showValidationError: false,
        showCopyError: false,
        showGenericCopyError: false,
        programsLoading: false,
      });
      return;
    }

    // A block entry clears the previous tab and client. Follow that URL
    // instead of the state left by the previous opening.
    const cleanBlockOpen =
      signatureChanged &&
      !ownerRefresh &&
      !!query.destinationCycleId &&
      !query.fetchType &&
      !queryOwner &&
      (this.state.fetchType !== "template" ||
        this.state.sourceCycleId ||
        this.state.programOrder);
    if (cleanBlockOpen) {
      this._expectedOwner = "";
      this._expectedType = "";
      this.setState({
        fetchType: "template",
        sourceOwner: coachId,
        sourceCycleId: "",
        programOrder: "",
        showValidationError: false,
        showCopyError: false,
        showGenericCopyError: false,
        programsLoading: false,
      });
      return;
    }

    if (ownerRefresh) {
      this._expectedOwner = "";
      this._expectedType = "";
      this._resentFor = "";
      this._resentQuery = "";
      if (this.state.programsLoading) this.setState({ programsLoading: false });
      return;
    }

    // A slower response for the previous client rewrote the URL. Ask again
    // for the client that's actually selected, and keep the list in loading
    // until that response lands.
    if (
      localsChanged &&
      this.state.fetchType === "otherAthlete" &&
      selectedOwner &&
      queryOwner &&
      queryOwner !== selectedOwner &&
      !(this._resentFor === selectedOwner && this._resentQuery === queryOwner)
    ) {
      this._expectedOwner = selectedOwner;
      this._expectedType = "otherAthlete";
      this._resentFor = selectedOwner;
      this._resentQuery = queryOwner;
      if (!this.state.programsLoading) this.setState({ programsLoading: true });
      submit("/actions/refresh", {
        method: "GET",
        body: {
          fetchSourceOwner: selectedOwner,
          fetchType: "otherAthlete",
        },
      });
    }
  }

  handleAthleteChange(sourceOwner) {
    clearProgramFetch();
    const ownerId = String(sourceOwner);
    this.setState({
      sourceOwner: ownerId,
      sourceCycleId: "",
      programOrder: "",
      showValidationError: false,
      showCopyError: false,
      showGenericCopyError: false,
      programsLoading: true,
    });
    this.refreshCopiableOwner(ownerId, "otherAthlete");
  }

  handleFetchTypeChange(fetchType) {
    clearProgramFetch();
    const coachId = String(this.context.locals.activeUserToRequest._id);
    const sourceOwner = ["template", "store"].includes(fetchType)
      ? coachId
      : "";
    this.setState({
      fetchType,
      sourceOwner,
      sourceCycleId: "",
      programOrder: "",
      showValidationError: false,
      showCopyError: false,
      showGenericCopyError: false,
      programsLoading: !!sourceOwner,
    });
    if (sourceOwner) this.refreshCopiableOwner(sourceOwner, fetchType);
  }

  // The modal opens with the coach's cycles. Another client's programs are a
  // different owner, so the list has to be loaded again and the selection cleared.
  refreshCopiableOwner(ownerId, fetchType) {
    const next = String(ownerId || "");
    if (!next) return;
    const currentOwner = String(
      this.context.locals.query?.fetchSourceOwner || ""
    );
    const currentType = String(this.context.locals.query?.fetchType || "");
    const coachId = String(this.context.locals.activeUserToRequest._id);
    if (
      !currentOwner &&
      next === coachId &&
      (!currentType || currentType === fetchType)
    ) {
      this._expectedOwner = "";
      this._expectedType = "";
      // The handler may already have marked the list loading. This owner is
      // the one on screen, so let the dropdown open.
      this.setState({ programsLoading: false });
      return;
    }
    if (currentOwner === next && currentType === fetchType) {
      this._expectedOwner = "";
      this._expectedType = "";
      this.setState({ programsLoading: false });
      return;
    }
    this._expectedOwner = next;
    this._expectedType = fetchType;
    this._resentFor = "";
    this._resentQuery = "";
    submit("/actions/refresh", {
      method: "GET",
      body: { fetchSourceOwner: next, fetchType },
    });
  }

  async fetch(cycleId, destinationCycleId, programOrder, e) {
    e.preventDefault();
    if (this._sending || this.state.submited) return;
    const {
      query: { athlete },
      activeUserToRequest,
      activeUser,
    } = this.context.locals;
    const recipient = athlete
      ? String(athlete)
      : String(activeUserToRequest._id);
    const coachAvatar =
      !!activeUser.avatar && !!activeUser.avatar.file
        ? activeUser.avatar.file
        : null;
    const firebaseProjectId = activeUserToRequest.firebaseProjectId
      ? activeUserToRequest.firebaseProjectId
      : defaultFirebaseProjectId;
    this._sending = true;
    this.setState({
      submited: true,
      showCopyError: false,
      showGenericCopyError: false,
    });
    let connectionFailed = false;
    let genericFailed = false;
    let modalStillOpen = false;
    try {
      const result = await this.submitCopy({
        cycleId,
        destinationCycleId,
        programOrder,
        recipients: [recipient],
        subscription: this.props.subscription || false,
        status: this.context.locals.query.destinationCycleStatus
          ? this.context.locals.query.destinationCycleStatus
          : "draft",
        avatar: coachAvatar,
        firebaseProjectId,
        omitModified: true,
      });
      connectionFailed = !!result?.connectionFailed;
      genericFailed = !!result?.genericFailed;
      modalStillOpen = !!result?.modalStillOpen;
    } catch {
      genericFailed = true;
      modalStillOpen = true;
    } finally {
      this._sending = false;
    }
    if (this._unmounted) return;
    if (connectionFailed) {
      this.setState({
        submited: false,
        showCopyError: true,
        showGenericCopyError: false,
      });
      return;
    }
    if (genericFailed) {
      this.setState({
        submited: false,
        showCopyError: false,
        showGenericCopyError: true,
      });
      return;
    }
    // The copy landed and the modal is closing. Drop the open token before
    // unmount so Back cannot reopen it in the other mode.
    if (!modalStillOpen) clearProgramFetch();
    else this.setState({ submited: false });
  }

  // Posts through the same Rill router a native form submit uses, so the modal
  // can read the response headers (x-copy-failed / x-copy-error) instead of
  // being navigated away. Same pattern as fetchLibraryMetadataAction in
  // utils/submit-library-metadata-from-add-exercise.js; `req.original.socket.server`
  // is the router instance Rill's document adapter stores on the request.
  submitCopy(body) {
    if (!process.browser) {
      return Promise.resolve({
        connectionFailed: false,
        genericFailed: false,
        modalStillOpen: false,
      });
    }
    const rillServer = this.context.req?.original?.socket?.server;
    if (!rillServer) {
      return Promise.resolve({
        connectionFailed: false,
        genericFailed: true,
        modalStillOpen: true,
      });
    }
    // Lazy require: the document adapter touches window at load time.
    const { fetch: rillDocumentFetch } = require("@rill/http/adapter/document");
    return rillDocumentFetch(rillServer, {
      url: "/actions/cycle/send/?source=fetch",
      method: "POST",
      body,
    }).then(([, meta]) => {
      const headers = meta?.headers || {};
      const status = Number(meta?.status);
      const url = String(meta?.url || "");
      const connectionFailed = headers["x-copy-failed"] === "1";
      const genericFailed =
        headers["x-copy-error"] === "1" ||
        (!connectionFailed && Number.isFinite(status) && status >= 400);
      return {
        connectionFailed,
        genericFailed,
        modalStillOpen: /modal(?:%5B|\[)type(?:%5D|\])=fetch/.test(url),
      };
    });
  }

  render() {
    const {
      sourceOwner,
      sourceCycleId,
      fetchType = "template",
      programOrder,
      submited,
    } = this.state;
    const { locals } = this.context;
    const { isAllowToViewAllOrganizationsAthletes, t } = this.props;
    const {
      query = {},
      activeUserToRequest: { referAsClients },
      activeUser: { athletes },
      allAthletesFromOrganization,
      organizationOwner,
      organizationPermissions,
      organizationRoles,
    } = locals;
    const copiableCycles = locals.copiableCycles || [];
    const copiableCyclesError = !!locals.copiableCyclesError;
    const { userCanAccessToPush } = getUserAccess(
      locals.activeUser,
      organizationOwner,
      organizationPermissions,
      organizationRoles
    );

    const {
      destinationCycleId,
      destinationCycleName,
      destinationCycleStatus,
      athlete: queryAthlete,
    } = query;
    const copyingToTemplates =
      !destinationCycleId &&
      !queryAthlete &&
      !locals.athlete &&
      destinationCycleStatus === "created";
    // Block mode is the entry that copies into an existing cycle (Select
    // Block / Add Block). Whole-program mode has no destination.
    const prefix = t(
      destinationCycleId ? "fetchModal.blockPrefix" : "fetchModal.programPrefix"
    );
    let validationErrorKey = "fetchModal.selectProgramRequired";
    if (fetchType === "otherAthlete" && !sourceOwner) {
      validationErrorKey = referAsClients
        ? "fetchModal.selectClientRequired"
        : "fetchModal.selectAthleteRequired";
    }
    const athletesOptions = [];

    if (
      !allAthletesFromOrganization ||
      !isAllowToViewAllOrganizationsAthletes
    ) {
      _.each(athletes, (athlete) => {
        athletesOptions.push({
          value: athlete._id,
          label: athlete.name,
        });
      });
    } else {
      _.each(allAthletesFromOrganization, ({ athleteId, athleteName }) => {
        athletesOptions.push({
          value: athleteId,
          label: athleteName,
        });
      });
    }

    const loadedOwner = String(
      query.fetchSourceOwner || locals.activeUserToRequest._id
    );
    // A late response for another owner still sits in locals until the
    // selected owner's request returns. Don't paint that list.
    const programsLoading =
      this.state.programsLoading ||
      (!!sourceOwner && loadedOwner !== String(sourceOwner));
    const cyclesOptions = [];
    const cycleCanBeCopied = destinationCycleId
      ? cycleHasCopyableBlock
      : cycleHasNoEmptyWorkouts;
    const cyclesForOptions = programsLoading ? [] : copiableCycles;
    cyclesForOptions.forEach((cycle) => {
      if (
        !cycle?.isDeleted &&
        cycleOwnerId(cycle) === String(sourceOwner) &&
        cycle.type === TYPES_MAP[fetchType] &&
        (!cycle.storeType || cycle.storeType === "programs") &&
        cycleCanBeCopied(cycle)
      ) {
        cyclesOptions.push({
          value: cycle._id,
          label: `${cycle.name}${
            fetchType === "store" ? ` (${_.capitalize(cycle.status)})` : ""
          }`,
        });
      }
    });

    const sourceCycle = programsLoading
      ? undefined
      : _.find(copiableCycles, { _id: sourceCycleId });
    const selectedProgram = programByOrder(sourceCycle?.programs, programOrder);
    const selectedCycleIsCopyable =
      !!sourceCycle && !sourceCycle.isDeleted && cycleCanBeCopied(sourceCycle);
    const selectedBlockIsCopyable =
      !destinationCycleId || programHasNoEmptyWorkouts(selectedProgram);

    const isValid =
      sourceOwner &&
      selectedCycleIsCopyable &&
      !!destinationCycleId === !!programOrder &&
      selectedBlockIsCopyable;

    const fetchOptions = [
      { value: "template", label: t("fetchModal.fromTemplate") },
      {
        value: "otherAthlete",
        label: referAsClients
          ? t("fetchModal.fromAnotherClient")
          : t("fetchModal.fromAnotherAthlete"),
      },
    ];
    if (userCanAccessToPush) {
      fetchOptions.push({ value: "store", label: t("fetchModal.fromStore") });
    }

    return (
      <Modal
        toClear={CLEAR_ON_CLOSE}
        className="fetch-modal"
        title={t("fetchModal.copyTitle", { prefix })}
        onClose={() => clearProgramFetch()}
      >
        <div className="body">
          <OptionToggler
            name="fetchType"
            options={fetchOptions}
            defaultValue={fetchType}
            onSelectFn={(value) => this.handleFetchTypeChange(value)}
          />

          {fetchType === "otherAthlete" && (
            <>
              <label>
                {referAsClients
                  ? t("fetchModal.selectClientLabel")
                  : t("fetchModal.selectAthleteLabel")}
              </label>
              <input
                type="hidden"
                value={sourceOwner}
                name="fetch-modal-athletes"
              />
              <SearchDropdown
                name="fetch-modal-athletes"
                options={athletesOptions}
                handleChange={(option) =>
                  this.handleAthleteChange(option.value)
                }
                title={
                  referAsClients
                    ? t("fetchModal.copyFromClient", { prefix })
                    : t("fetchModal.copyFromAthlete", { prefix })
                }
                value={
                  athletesOptions.find(
                    (athlete) => String(athlete.value) === String(sourceOwner)
                  )?.label || t("fetchModal.select")
                }
                valueKey="label"
              />
            </>
          )}
          {(sourceOwner ||
            (!destinationCycleId && fetchType === "otherAthlete")) && (
            <>
              <label>{t("fetchModal.selectProgramLabel")}</label>
              <input
                type="hidden"
                value={sourceCycleId}
                name="fetch-modal-cycles"
              />
              <SearchDropdown
                key={`programs-${sourceOwner || "none"}`}
                options={cyclesOptions}
                disabled={programsLoading}
                handleChange={(option) =>
                  this.setState({
                    sourceCycleId: option.value,
                    programOrder: "",
                    showValidationError: false,
                    showCopyError: false,
                    showGenericCopyError: false,
                  })
                }
                value={
                  programsLoading
                    ? t("common:loading")
                    : cyclesOptions.find(
                        (option) => option.value === sourceCycleId
                      )?.label || t("fetchModal.select")
                }
                valueKey="label"
              />
              {programsLoading ? (
                <p className="p4 opacity-70 mt-2">{t("common:loading")}</p>
              ) : copiableCyclesError ? (
                <p className="chip danger mt-2">
                  {t("fetchModal.programsError")}
                </p>
              ) : (
                cyclesOptions.length === 0 &&
                (fetchType !== "otherAthlete" || sourceOwner) && (
                  <p className="p4 opacity-70 mt-2">
                    {t("fetchModal.noPrograms")}
                  </p>
                )
              )}
            </>
          )}

          {!destinationCycleId && selectedCycleIsCopyable && (
            <div className="group fetch-cycle-summary">
              <p className="chip info">
                {copyingToTemplates
                  ? t("fetchModal.templatesInfo", { prefix })
                  : t("fetchModal.athletePortalInfo", {
                      prefix,
                      athleteName: _.get(locals, "athlete.name", ""),
                    })}
              </p>

              <AccordionRadio
                cycleId={sourceCycleId}
                cycleName={sourceCycle.name}
                programsCount={sourceCycle.programs.length}
              >
                {_.get(sourceCycle, "programs", []).map((program, index) => (
                  <div className="program" key={`${program.order}-${index}`}>
                    <span className="count">{program.order}</span>
                    <span className="name">{program.name}</span>
                  </div>
                ))}
              </AccordionRadio>
            </div>
          )}

          {destinationCycleId && selectedCycleIsCopyable && (
            <Select
              name="fetchmodal-programs"
              title={t("fetchModal.selectBlockTitle")}
              options={sourceCycle.programs
                .filter(programHasNoEmptyWorkouts)
                .map((prog) => ({
                  value: prog.order,
                  label: prog.name,
                }))}
              // openOnClick={false}

              onChange={(programOrder) => this.setState({ programOrder })}
              value={programOrder || t("fetchModal.select")}
            />
          )}

          {destinationCycleId &&
            selectedCycleIsCopyable &&
            programOrder &&
            selectedBlockIsCopyable && (
              <div className="group fetch-program-summary">
                <p className="chip info">
                  {t("fetchModal.programInfo", {
                    prefix,
                    destinationCycleName,
                  })}
                </p>

                <AccordionRadio
                  cycleId={sourceCycleId}
                  cycleName={sourceCycle.name}
                  programsCount={sourceCycle.programs.length}
                >
                  <div className="program">
                    <span className="count" />
                    <span className="name">{selectedProgram.name}</span>
                  </div>
                </AccordionRadio>
              </div>
            )}

          {this.state.showValidationError && !destinationCycleId && (
            <p className="chip danger mt-6">{t(validationErrorKey)}</p>
          )}
          {this.state.showCopyError && (
            <p className="chip danger mt-6">{t("fetchModal.copyFailed")}</p>
          )}
          {this.state.showGenericCopyError && (
            <p className="chip danger mt-6">{t("fetchModal.copyError")}</p>
          )}
        </div>

        <footer>
          <a
            role="button"
            data-button-type="secondary"
            data-action="cancel"
            href={`/actions/refresh?modal=&${CLEAR_ON_CLOSE.join("=&")}=`}
            onClick={() => clearProgramFetch()}
          >
            {t("common:cancel")}
          </a>
          <button
            type="submit"
            data-button-type="primary"
            aria-busy={submited ? "true" : undefined}
            disabled={
              submited || (destinationCycleId && !isValid) ? true : undefined
            }
            onClick={(e) => {
              if (submited) {
                e.preventDefault();
                return;
              }
              if (!isValid) {
                e.preventDefault();
                if (!destinationCycleId) {
                  this.setState({ showValidationError: true });
                }
                return;
              }
              this.fetch(sourceCycleId, destinationCycleId, programOrder, e);
            }}
          >
            {submited
              ? t("fetchModal.copying")
              : destinationCycleId
                ? t("fetchModal.addBlockButton")
                : t("fetchModal.addProgramButton")}
          </button>
        </footer>
      </Modal>
    );
  }
}

FetchCycleModal.contextType = RillContext;

export default withTranslation(["coaching-portal", "common"])(FetchCycleModal);
